import { useEffect, useMemo, useRef, useState } from 'react'
import { Divider, Switch } from 'antd'
import classNames from 'classnames'
import styled from '@emotion/styled'

import { useHover } from 'ahooks'
import MarkdownIt from 'markdown-it'
import { Editor } from '@/pages/datastores/document-detail/paragraph/editor'
import type { Paragraph } from '@/pages/datastores/types/paragraph.ts'
import { MAX_SPLIT_SIZE } from '@/features/datastore/constant'
import { useModal } from '@/hooks/useModal.tsx'

import KeywordsView from '@/pages/datastores/components/KeywordsView.tsx'
import { ParagraphExtraInfoStatus } from '@/apis/datastore/model'
import { NormalParagraphEditorModal } from '../ParagraphItemEditorModal/NormalParagraphEditorModal'
import { Tips } from './components/Tips'
import {
  OperationAction,
  OperationActionType,
} from './components/OperationAction'

const BgWrapper = styled.div`
  border-color: rgba(225, 225, 229, 0.6);
  border-radius: 8px;
  width: 100%;
  height: 100%;
  top: 0px;
  left: 0px;
  position: absolute;
  pointer-events: none;
  content: '';
  background: linear-gradient(94deg, #68caff 0%, #684aff 62%, #963aff 99%);
  border-width: 0px;
`

export type ParagraphItem = Pick<
  Paragraph,
  | 'content'
  | 'isEdited'
  | 'enable'
  | 'key'
  | 'keywords'
  | 'extra_info'
  | 'chunk_id'
  | 'file_type'
  | 'file_name'
>

export interface NormalParagraphListItemProps {
  first?: boolean
  last?: boolean
  border?: boolean
  numText: string
  rank: number | string
  file_id: number
  activeId?: number
  paragraph: ParagraphItem
  onSplit?: (splitValue: [string, string]) => void
  onEnableChange: (enable: boolean) => void
  onEditorLoad: (editor: Editor) => void
  onEditorDestroy: () => void
  onEdit: () => void
  readonly?: boolean
  readyToMerge: boolean
  onDelete: (key: number | string) => void
  updateParagraphsItemById?: (value: Paragraph) => void
  handleGenerateAISummary: (id?: number) => void
  addParagraphsItem?: (item: Paragraph, targetId?: number) => void
  onSelect?: (key: number | string) => void
  onListItemClick?: (type: string, params: Record<string, any>) => void
}

const GenerateByAI = styled.div`
  font-weight: 500;
  font-variation-settings: 'opsz' auto;
  background: linear-gradient(100deg, #2cb5ff 0%, #6b4eff 62%, #963aff 99%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  z-index: 1;
  font-size: 12px;
  line-height: 14px;
`

const Wrap = styled.div<{ disabled: boolean; isEdited?: boolean }>`
  position: relative;
  &:hover {
    .info-box,
    .delete-btn {
      opacity: 1;
      width: auto;
    }
    .keywords-view {
      display: block;
    }
    .num-text {
      display: none;
    }
  }
  .paragraph-editor,
  .paragraph-editor > *:not(.paragraph-split-mark) {
    ${props => props.disabled && 'color: rgba(141, 141, 153, 0.6);'}
  }

  .edited-mark:before {
    content: '';
    width: 24px;
    height: 24px;
    background: #38a6ff;
    opacity: 0.4;
    position: absolute;
    top: -12px;
    left: -12px;
    pointer-events: none;
    display: ${props => (props.isEdited ? 'block' : 'none')};
    transform: rotate(45deg);
  }

  table {
    width: 100%;
    border-collapse: collapse;
    line-height: 22px;
    font-size: 14px;
  }

  caption {
    color: blue;
    font-size: 14px;
    height: 20px;
    line-height: 20px;
    font-weight: 600;
    margin-bottom: 6px;
    border: 1px solid #d9d9d9;
  }

  th {
    color: #fff;
    background-color: blue;
  }
  td {
    padding: 4px 8px;
    border: 1px solid #e1e1e5;
    cursor: pointer;
  }

  td:hover {
    // background: blue;
  }

  tr:nth-child(even) {
    background-color: #f2f2f2;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    max-width: 400px;
    max-height: 600px;
  }

  video {
    width: 100%;
    height: 100%;
    max-width: 400px;
    max-height: 600px;
  }

  // p {
  //     position: absolute;
  //   }

  @keyframes show-paragraph-active {
    0% {
      background-color: hsla(240deg, 0%, 100%, 1);
    }

    20% {
      background-color: hsla(240deg, 7%, 90%, 1);
    }

    100% {
      background-color: hsla(240deg, 0%, 100%, 1);
    }
  }

  .paragraph-blink {
    animation: show-paragraph-active linear 1s 2;
  }
`

export default function NormalParagraphListItem(
  props: NormalParagraphListItemProps,
) {
  const {
    border,
    numText,
    paragraph,
    readonly,
    readyToMerge,
    onEnableChange,
    onDelete,
    handleGenerateAISummary,
    file_id,
    onEditorLoad,
    onEditorDestroy,
    addParagraphsItem,
    updateParagraphsItemById,
    onListItemClick,
    onSelect,
    activeId,
  } = props
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const content = useMemo(() => {
    return paragraph.content?.replace(
      /<script[^>]*>[\s\S]*?<\/script>|<iframe[^>]*>[\s\S]*?<\/iframe>/gi,
      '',
    )
  }, [paragraph.content])
  const count = useMemo(() => {
    return paragraph?.content?.length
  }, [paragraph?.content?.length])
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [normalParagraphEditorModal] = useModal(NormalParagraphEditorModal)

  const hovering = useHover(wrapperRef.current)
  const editorRef = useRef<HTMLDivElement | null>(null)
  const editorInterface = useRef<Editor>()
  const loadEditor = () => {
    editorInterface.current = new Editor({
      dom: editorRef.current!,
      content,
      editEnable: false,
    })
    onEditorLoad(editorInterface.current)
    editorInterface.current.init()
  }

  useEffect(() => {
    loadEditor()
    return () => {
      editorInterface.current?.destroy()
      onEditorDestroy()
    }
  }, [])

  const chunkReadOnly = useMemo(() => {
    return (
      paragraph.extra_info?.chunk_status === ParagraphExtraInfoStatus.Pending ||
      paragraph.extra_info?.chunk_status === ParagraphExtraInfoStatus.Process ||
      paragraph.extra_info?.chunk_status === ParagraphExtraInfoStatus.Error
    )
  }, [paragraph.extra_info?.chunk_status])

  const moreThanMaxSize = count > MAX_SPLIT_SIZE

  const handleSelect = () => {
    onSelect?.((paragraph.chunk_id || (paragraph as any).originalChunkId) ?? 0)
  }

  useEffect(() => {
    if (activeId !== paragraph.chunk_id) return
    handleSelect()
  }, [activeId])

  const previewContent = useMemo(() => {
    return new MarkdownIt({
      html: true,
      linkify: false,
      breaks: true,
    }).render(content)
  }, [content])

  const handleOpenModal = () => {
    normalParagraphEditorModal.open({
      operationType: 'update',
      file_id,
      title: `编辑段落${numText}`,
      chunk_id: paragraph.chunk_id!,
      paragraph,
      callback: updateParagraphsItemById!,
    })
  }

  const addParagraph = () => {
    normalParagraphEditorModal.open({
      operationType: 'add',
      file_id: file_id!,
      title: '添加段落',
      insert_above_chunk_id: paragraph.chunk_id,
      paragraph: {
        content: '',
        enable: true,
        extra_info: {},
      },
      callback: values => {
        addParagraphsItem?.(values, paragraph.chunk_id)
      },
    })
  }

  const handleActionClick = (type: OperationActionType) => {
    switch (type) {
      case OperationActionType.ADD:
        addParagraph()
        break
      case OperationActionType.DELETE:
        onDelete(paragraph.key)
        break
      case OperationActionType.EDIT:
        handleOpenModal()
        break
      case OperationActionType.AI:
        handleGenerateAISummary(paragraph?.chunk_id!)
        break
    }
  }

  const actionList = useMemo(() => {
    return (
      paragraph?.extra_info?.enable_ai_support
        ? Object.values(OperationActionType)
        : Object.values(OperationActionType).filter(
            item => item !== OperationActionType.AI,
          )
    ) as any
  }, [paragraph?.extra_info?.chunk_status])

  const bgStyle = useMemo(() => {
    if (
      paragraph?.extra_info?.chunk_status ===
        ParagraphExtraInfoStatus.Pending ||
      paragraph?.extra_info?.chunk_status === ParagraphExtraInfoStatus.Process
    ) {
      return <BgWrapper />
    } else if (
      paragraph?.extra_info?.chunk_status === ParagraphExtraInfoStatus.Error
    ) {
      return (
        <div
          style={{
            border: '1px solid #FF5219',
            width: '100%',
            height: '100%',
            position: 'absolute',
            borderRadius: '8px',
          }}
        ></div>
      )
    }
    return null
  }, [paragraph?.extra_info?.chunk_status])

  return (
    <Wrap
      disabled={!paragraph.enable}
      isEdited={paragraph.isEdited}
      onClick={() => {
        if (paragraph?.extra_info?.page_bboxes) {
          onListItemClick?.('highlight', paragraph?.extra_info?.page_bboxes)
        }
      }}
      className={classNames(
        'ml-24px relative rounded-[8px] overflow-hidden betteryeah-markdown',
      )}
    >
      {bgStyle}
      <div className='relative m-1px overflow-hidden box-border'>
        {paragraph?.extra_info?.chunk_status && (
          <div className='rounded-[8px_8px_0_0] overflow-hidden'>
            <Tips
              state={paragraph?.extra_info?.chunk_status}
              showBtn={paragraph?.extra_info.enable_ai_support}
              onClick={() => {
                handleGenerateAISummary(paragraph.chunk_id!)
              }}
              text={
                paragraph?.extra_info?.chunk_status ===
                ParagraphExtraInfoStatus.Error
                  ? paragraph?.extra_info?.error_text
                  : 'AI增强处理中，段落不可编辑'
              }
            />
          </div>
        )}
        <div
          ref={wrapperRef}
          className={classNames('pt-16 pb-20 bg-white border-1', {
            'border-0': !border,
            border,
            '!b-primary': readyToMerge && !readonly,
            'b-white': readonly || chunkReadOnly,
            'hover:b-primary': !readonly,
            'rounded-[8px]':
              paragraph.extra_info?.chunk_status !==
                ParagraphExtraInfoStatus.Error &&
              paragraph.extra_info?.chunk_status !==
                ParagraphExtraInfoStatus.Process &&
              paragraph.extra_info?.chunk_status !==
                ParagraphExtraInfoStatus.Pending,
            'rounded-[0_0_8px_8px]':
              paragraph.extra_info?.chunk_status ===
                ParagraphExtraInfoStatus.Error ||
              paragraph.extra_info?.chunk_status ===
                ParagraphExtraInfoStatus.Process ||
              paragraph.extra_info?.chunk_status ===
                ParagraphExtraInfoStatus.Pending,
            '!b-error': (moreThanMaxSize && !readonly) || deleteConfirmOpen,
            'paragraph-blink': activeId === paragraph.chunk_id,
          })}
        >
          <div className='flex-center-between px-20 gap-8 flex-1 w-full overflow-hidden'>
            <div className='w-full overflow-hidden'>
              <span
                className={classNames(
                  'num-text text-[12px] !line-height-26px c-font_1 font-normal py-1 h-[26px]',
                  {
                    hidden: hovering,
                  },
                )}
              >
                {numText}
              </span>
              <div
                className={classNames(
                  `${hovering ? 'block' : 'hidden'}`,
                  'keywords-view',
                )}
              >
                {paragraph.isEdited ? (
                  <div className='h-[26px] flex items-center'>
                    <span className='font-normal text-[12px] c-font_1 line-height-20px'>
                      关键词
                    </span>
                    <span className='ml-8 font-normal line-height-20px text-[12px] c-font_1 c-op-60'>
                      【保存】后，重新生成关键词
                    </span>
                  </div>
                ) : (
                  <KeywordsView
                    value={paragraph.keywords ?? []}
                    className='flex flex-1'
                  />
                )}
              </div>
            </div>
            <div className='flex items-center shrink-0'>
              <div
                className={classNames('flex items-center info-box', {
                  'op-0': !hovering,
                })}
              >
                {/* <span
                  className={classNames('c-font_1 text-[12px] font-normal', {
                    'c-error': moreThanMaxSize,
                  })}
                >
                  <span>共</span>
                  <span className='mx-[3px]'>
                    <span
                      className={classNames({
                        'c-#8d8d99': true,
                        '!c-error': moreThanMaxSize,
                      })}
                    >
                      {count}
                    </span>
                    {!readonly && !chunkReadOnly && (
                      <span> / {MAX_SPLIT_SIZE}</span>
                    )}
                  </span>
                  <span>字</span>
                </span> */}
                {!paragraph.enable && !chunkReadOnly && (
                  <div className='ml-12 py-2 text-12px px-8 rounded-4px c-#FE9700 bg-#FE9700 bg-op-12'>
                    <span className='w-8px h-8px rounded-full mr-6 inline-block bg-#FE9700'></span>
                    <span>已禁用</span>
                  </div>
                )}
                {!readonly && !chunkReadOnly && (
                  <>
                    <Divider type='vertical' className='mx-12' />
                    <Switch
                      size='small'
                      checked={paragraph.enable}
                      onChange={onEnableChange}
                    />
                  </>
                )}
              </div>
              {!readonly &&
                paragraph.extra_info?.chunk_status !==
                  ParagraphExtraInfoStatus.Pending &&
                paragraph.extra_info?.chunk_status !==
                  ParagraphExtraInfoStatus.Process && (
                  <span
                    className={classNames('flex items-center delete-btn', {
                      'op-0': !deleteConfirmOpen,
                      'w-0': !deleteConfirmOpen,
                    })}
                  >
                    <Divider type='vertical' className='mx-12' />
                    <OperationAction
                      actionList={actionList as any}
                      onActionClick={handleActionClick}
                      deleteConfirmOpen={deleteConfirmOpen}
                      setDeleteConfirmOpen={setDeleteConfirmOpen}
                    />
                  </span>
                )}
            </div>
          </div>
          <div
            className={classNames('mt-12 px-20px editor-wrap relative', {
              'op-40': !paragraph.enable,
            })}
            dangerouslySetInnerHTML={{ __html: previewContent }}
          />
          <div
            className='h-0px w-0 invisible mt-12 ml-8px text-14px line-height-20px'
            ref={editorRef}
          />
          {paragraph?.extra_info?.ai_created && (
            <div className='absolute right-24px bottom-14px flex items-center'>
              <GenerateByAI>AI生成</GenerateByAI>
              {/* <div
              className='px-4px py-2px bg-[rgba(98,105,153,0.08)] rounded-4px text-12px cursor-pointer ml-12px c-#8D8D99'
              onClick={() => {
                alert('晚点上')
              }}
            >
              查看原段落
            </div> */}
            </div>
          )}
        </div>
      </div>
    </Wrap>
  )
}
